import React, { useEffect, useRef, useState } from 'react'
import { Alert, Box } from '@mui/material'
import { useToasts } from 'react-toast-notifications'
import { describeFailure } from '../../api/failureReason'
import FailureMessage from '../../components/FailureMessage'
import { usePatientConversation } from '../../api/patientConversation'
import { SHARED_CHAT_ROLE } from './useHideSharedChat'
import { useMyCareTeamRoles } from './useMyCareTeamRoles'
import { ForwardedFeedItem, PendingComment } from './types'
import EventFeedView from './EventFeedView'
import PatientChatComposer from './PatientChatComposer'
import { useChatEventFeed } from './useChatEventFeed'
import { usePatientChatEditing } from './usePatientChatEditing'

type Props = {
  uid: string
  careTeamChatOpen?: boolean
  onForwardToCareTeam?: (item: ForwardedFeedItem) => void
  onComposerHeightChange?: (height: number) => void
}

const sendBlockedNotice = (roles: string[] | undefined): string | null => {
  if (!roles) {
    return 'Your care-team role has not loaded yet, so the message cannot be sent.'
  }
  return roles.length > 1
    ? 'You hold more than one role on this care team, so the message cannot be sent to a chat.'
    : null
}

const roleChatFor = (roles: string[] | undefined): string | null =>
  roles?.length === 1 && roles[0] !== SHARED_CHAT_ROLE ? roles[0] : null

// One feed, as in Standard: the patient's messages sit among the activity, with the composer under them.
const FeedChatPanel = ({ uid, careTeamChatOpen = false, onForwardToCareTeam, onComposerHeightChange }: Props) => {
  const feed = useChatEventFeed(uid)
  const myRoles = useMyCareTeamRoles()
  const conversation = usePatientConversation(uid)
  const [editing, setEditing] = usePatientChatEditing()
  const [pendingComment, setPendingComment] = useState<PendingComment | null>(null)
  const composerRef = useRef<HTMLDivElement>(null)
  const { addToast } = useToasts()

  // Rethrown so the composer keeps the draft.
  const reportFailure = (summary: string) => (cause: unknown) => {
    addToast(<FailureMessage failure={describeFailure(summary, cause)} />, { appearance: 'error' })
    throw cause
  }

  useEffect(() => {
    const composer = composerRef.current
    if (!composer || !onComposerHeightChange || typeof ResizeObserver === 'undefined') {
      return undefined
    }
    const observer = new ResizeObserver(() => onComposerHeightChange(composer.offsetHeight))
    observer.observe(composer)
    return () => observer.disconnect()
  }, [onComposerHeightChange])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <EventFeedView
          uid={uid}
          feed={feed}
          onCommentInChat={
            careTeamChatOpen
              ? undefined
              : (comment) => {
                  setEditing(null)
                  setPendingComment(comment)
                }
          }
          onEditMessage={careTeamChatOpen ? undefined : setEditing}
          onForwardToCareTeam={onForwardToCareTeam}
        />
      </Box>

      <Box ref={composerRef}>
        {conversation.error && (
          <Alert severity="error" sx={{ mx: 1 }}>
            <FailureMessage failure={describeFailure('Failed to load this conversation.', conversation.error)} />
          </Alert>
        )}
        <PatientChatComposer
          uid={uid}
          careTeamChatOpen={careTeamChatOpen}
          inAppChatEnabled={conversation.inAppChatEnabled}
          sending={conversation.sending}
          editing={editing}
          onCancelEdit={() => setEditing(null)}
          onSendChat={(text, linksTo, fileIDs) =>
            conversation
              .sendChat(text, linksTo, fileIDs, roleChatFor(myRoles))
              .catch(reportFailure('Failed to send this message — it has not reached the patient.'))
          }
          sendBlockedNotice={sendBlockedNotice(myRoles)}
          onEdit={(messageID, text) =>
            conversation.editChat(messageID, text).catch(reportFailure('Failed to edit this message.'))
          }
          pendingComment={pendingComment}
          onClearPendingComment={() => setPendingComment(null)}
        />
      </Box>
    </Box>
  )
}

export default FeedChatPanel
